/**
 * DuplicateJobsPanel — Collapsible card showing flagged duplicate jobs.
 * Groups by (customer + address + service) within a 24-hour window.
 * The oldest job in each group is the "original" (kept). Newer matches
 * appear with a checkbox so a manager can select + delete them.
 */
import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronRight, Copy, Trash2, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function DuplicateJobsPanel({ onAfterDelete }) {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // id -> bool
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/management/duplicate-jobs?window_hours=24");
      setGroups(data.groups || []);
      setTotal(data.total_duplicates || 0);
      setSelected({});
    } catch (err) {
      console.error("dup-jobs failed", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} duplicate job(s)? Originals will be kept. This action is auditable but cannot be undone from the UI.`)) {
      return;
    }
    setDeleting(true);
    try {
      const { data } = await API.post("/management/duplicate-jobs/delete", { request_ids: selectedIds });
      const deleted = (data.deleted || []).length;
      const skipped = (data.skipped || []).length;
      if (deleted) toast.success(`${deleted} duplicate(s) removed`);
      if (skipped) toast.warning(`${skipped} skipped (protected status)`);
      await load();
      onAfterDelete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (total === 0 && !loading) return null;

  return (
    <Card className="border border-rose-300 bg-rose-50/40" data-testid="duplicate-jobs-panel">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((o) => !o); } }}
        className="w-full flex items-center justify-between gap-2 p-3 hover:bg-rose-50 rounded-t-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-rose-400"
        data-testid="duplicate-jobs-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-rose-700" /> : <ChevronRight className="w-4 h-4 text-rose-700" />}
          <Copy className="w-4 h-4 text-rose-700" />
          <span className="text-sm font-semibold text-rose-900">
            Duplicate Jobs Detected
          </span>
          <span className="ml-1 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold"
                data-testid="duplicate-jobs-count">
            {total}
          </span>
          <span className="hidden sm:inline text-[11px] text-rose-700">
            (same customer + address + service within 24h)
          </span>
        </div>
        <span className="flex items-center gap-1">
          {selectedIds.length > 0 && open && (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); handleDelete(); }}
              disabled={deleting}
              className="h-7 bg-rose-600 hover:bg-rose-700 text-white text-xs"
              data-testid="duplicate-jobs-delete-btn"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Delete {selectedIds.length}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); load(); }}
            className="h-7 w-7 p-0 hover:bg-rose-100"
            data-testid="duplicate-jobs-refresh"
            aria-label="Refresh duplicate jobs"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-rose-700 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </span>
      </div>

      {open && (
        <div className="border-t border-rose-200 divide-y divide-rose-100 max-h-[60vh] overflow-y-auto">
          {loading && groups.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-rose-700">Loading…</div>
          ) : groups.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-rose-700">No duplicates found.</div>
          ) : groups.map((g) => (
            <div key={g.group_key} className="p-3 space-y-2" data-testid={`duplicate-group-${g.original.id}`}>
              <div className="text-[11px] uppercase tracking-wide text-rose-700 font-bold">
                Group: {g.original.customer_name} · {g.original.service_type_label}
              </div>

              {/* Original (kept) */}
              <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 flex items-center gap-2"
                   data-testid={`duplicate-original-${g.original.id}`}>
                <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-emerald-900">ORIGINAL — KEPT</span>
                    <span className="font-mono text-[10px] text-emerald-700">{g.original.request_code}</span>
                    <span className="text-[10px] text-emerald-700">status: {g.original.current_status}</span>
                  </div>
                  <div className="text-[11px] text-emerald-900 truncate">
                    {g.original.property_address_line1}{g.original.property_city ? `, ${g.original.property_city}` : ""}
                  </div>
                  <div className="text-[10px] text-emerald-700">Created {fmt(g.original.created_at)}</div>
                </div>
              </div>

              {/* Duplicates */}
              {g.duplicates.map((d) => (
                <label key={d.id}
                       className="flex items-center gap-2 rounded-md border border-rose-200 bg-white px-3 py-2 cursor-pointer hover:bg-rose-50"
                       data-testid={`duplicate-row-${d.id}`}>
                  <Checkbox
                    checked={!!selected[d.id]}
                    onCheckedChange={() => toggle(d.id)}
                    data-testid={`duplicate-checkbox-${d.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-rose-900">DUPLICATE</span>
                      <span className="font-mono text-[10px] text-rose-700">{d.request_code}</span>
                      <span className="text-[10px] text-rose-600">status: {d.current_status}</span>
                    </div>
                    <div className="text-[11px] text-zinc-700 truncate">
                      {d.property_address_line1}{d.property_city ? `, ${d.property_city}` : ""}
                    </div>
                    <div className="text-[10px] text-zinc-500">Created {fmt(d.created_at)}</div>
                  </div>
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
